import fs from 'fs/promises';
import fsSync from 'fs';
import path from 'path';
import { Worker } from 'worker_threads';
import type { WorkerOptions } from 'worker_threads';
import { createHash } from 'crypto';

import type { Logger } from 'pino';

import type { FileRecord } from '../types';
import type { FilesRepo, HashResult } from '../db/FilesRepo';

interface HashMessage {
  type: 'hash';
  taskId: number;
  absPath: string;
  relPath: string;
}

interface HashResultMessage {
  type: 'hash-result';
  taskId: number;
  contentSha256: string;
  serviceFileHash: string;
}

interface HashErrorMessage {
  type: 'hash-error';
  taskId: number;
  error: string;
  code?: string;
}

type WorkerMessage = HashResultMessage | HashErrorMessage;

type PendingTask = {
  resolve(value: { contentSha256: string; serviceFileHash: string }): void;
  reject(reason: Error): void;
};

class HashWorker {
  private readonly worker: Worker;
  private readonly pending = new Map<number, PendingTask>();
  private nextTaskId = 1;
  private readonly log: Logger;
  private terminating = false;

  constructor(logger: Logger) {
    this.log = logger.child({ scope: 'hash-worker' });
    const workerUrl = new URL('./hash/WorkerThread.js', import.meta.url);
    const execArgv = process.execArgv.filter(
      (arg) => !arg.startsWith('--input-type'),
    );
    const workerOptions: WorkerOptions & { type?: 'module' | 'commonjs' } = {
      name: 'coderule-hasher',
      execArgv,
    };
    if (workerUrl.pathname.endsWith('.js')) {
      workerOptions.type = 'module';
    }
    this.worker = new Worker(workerUrl, workerOptions);
    this.worker.on('message', (message: WorkerMessage) =>
      this.onMessage(message),
    );
    this.worker.on('error', (error) => this.handleWorkerError(error));
    this.worker.on('exit', (code) => {
      if (code !== 0 && !this.terminating) {
        this.handleWorkerError(
          new Error(`Hasher worker exited with code ${code}`),
        );
      }
    });
  }

  async terminate(): Promise<void> {
    this.terminating = true;
    for (const [, pending] of this.pending) {
      pending.reject(new Error('Hasher worker terminated'));
    }
    this.pending.clear();
    await this.worker.terminate();
  }

  private onMessage(message: WorkerMessage) {
    const pending = this.pending.get(message.taskId);
    if (!pending) {
      this.log.warn(
        { taskId: message.taskId },
        'Received message for unknown task',
      );
      return;
    }
    this.pending.delete(message.taskId);
    if (message.type === 'hash-result') {
      pending.resolve({
        contentSha256: message.contentSha256,
        serviceFileHash: message.serviceFileHash,
      });
    } else {
      const error = new Error(message.error);
      (error as any).code = message.code;
      pending.reject(error);
    }
  }

  private handleWorkerError(error: Error) {
    this.log.error({ err: error }, 'Hasher worker error');
    for (const [, pending] of this.pending) {
      pending.reject(error);
    }
    this.pending.clear();
  }

  compute(
    absPath: string,
    relPath: string,
  ): Promise<{ contentSha256: string; serviceFileHash: string }> {
    const taskId = this.nextTaskId++;
    const payload: HashMessage = {
      type: 'hash',
      taskId,
      absPath,
      relPath,
    };
    return new Promise((resolve, reject) => {
      this.pending.set(taskId, { resolve, reject });
      this.worker.postMessage(payload);
    });
  }
}

export interface HasherOptions {
  rootPath: string;
  filesRepo: FilesRepo;
  logger: Logger;
}

export class Hasher {
  private worker: HashWorker | null = null;
  private readonly log: Logger;
  private inlineMode: boolean;
  private readonly ownerId: string;
  private readonly leaseDurationMs: number;

  constructor(private readonly options: HasherOptions) {
    this.log = options.logger.child({ scope: 'hasher' });
    this.inlineMode = process.env.CODERULE_HASHER_INLINE === '1';
    this.ownerId = `hasher-${process.pid}-${Date.now()}`;
    const leaseFromEnv = process.env.CODERULE_HASH_LEASE_MS
      ? Number.parseInt(process.env.CODERULE_HASH_LEASE_MS, 10)
      : Number.NaN;
    this.leaseDurationMs =
      Number.isFinite(leaseFromEnv) && leaseFromEnv > 0 ? leaseFromEnv : 30_000;
    if (!this.inlineMode) {
      try {
        this.worker = new HashWorker(this.log);
      } catch (error) {
        this.log.warn(
          { err: error },
          'Failed to start hasher worker, falling back to inline hashing',
        );
        this.worker = null;
        this.inlineMode = true;
      }
    }
  }

  async close(): Promise<void> {
    if (this.worker) {
      await this.worker.terminate();
    }
  }

  private async yieldToEventLoop(): Promise<void> {
    await new Promise<void>((resolve) => setImmediate(resolve));
  }

  private resolveAbsolutePath(record: FileRecord): string {
    if (path.isAbsolute(record.display_path)) {
      return record.display_path;
    }
    return path.join(this.options.rootPath, record.rel_path);
  }

  private async ensureExists(
    absPath: string,
    record: FileRecord,
  ): Promise<boolean> {
    try {
      await fs.access(absPath);
      return true;
    } catch (error: any) {
      this.log.warn(
        { err: error, relPath: record.rel_path },
        'File missing before hashing',
      );
      this.options.filesRepo.markMissing(record.rel_path);
      return false;
    }
  }

  private async computeHash(
    absPath: string,
    relPath: string,
  ): Promise<{ contentSha256: string; serviceFileHash: string }> {
    if (this.inlineMode || !this.worker) {
      return this.hashInline(absPath, relPath);
    }

    return this.worker.compute(absPath, relPath);
  }

  private async hashInline(
    absPath: string,
    relPath: string,
  ): Promise<{ contentSha256: string; serviceFileHash: string }> {
    return new Promise((resolve, reject) => {
      const content = createHash('sha256');
      const service = createHash('sha256');
      service.update(relPath);
      service.update('\n');

      const stream = fsSync.createReadStream(absPath);
      stream.on('data', (chunk) => {
        content.update(chunk);
        service.update(chunk);
      });
      stream.on('error', (error) => {
        reject(error);
      });
      stream.on('end', () => {
        resolve({
          contentSha256: content.digest('hex'),
          serviceFileHash: service.digest('hex'),
        });
      });
    });
  }

  async processBatch(limit: number): Promise<boolean> {
    const now = Date.now();
    const requeued = this.options.filesRepo.requeueExpiredHashing(now);
    if (requeued > 0) {
      this.log.debug({ requeued }, 'Requeued expired hashing leases');
    }

    const dirty = this.options.filesRepo.claimDirty(
      limit,
      this.ownerId,
      this.leaseDurationMs,
    );
    if (dirty.length > 0) {
      this.log.debug({ count: dirty.length }, 'Hashing claimed files');
    }
    if (dirty.length === 0) {
      return false;
    }
    const successes: HashResult[] = [];
    const failures: number[] = [];

    for (const record of dirty) {
      const absPath = this.resolveAbsolutePath(record);
      const exists = await this.ensureExists(absPath, record);
      if (!exists) {
        if (this.inlineMode) {
          await this.yieldToEventLoop();
        }
        continue;
      }

      try {
        const result = await this.computeHash(absPath, record.rel_path);
        successes.push({
          id: record.id,
          contentSha256: result.contentSha256,
          serviceFileHash: result.serviceFileHash,
          hashOwner: record.hash_owner ?? this.ownerId,
          hashStartedAt: record.hash_started_at ?? 0,
        });
      } catch (error: any) {
        if (error?.code === 'ENOENT') {
          this.log.debug(
            { relPath: record.rel_path },
            'File disappeared during hashing',
          );
          this.options.filesRepo.markMissing(record.rel_path);
        } else {
          this.log.warn(
            { err: error, relPath: record.rel_path },
            'Failed to hash file',
          );
          failures.push(record.id);
        }
      }

      // When hashing inline (fallback), yield after each file to ensure MCP stays responsive
      if (this.inlineMode) {
        await this.yieldToEventLoop();
      }
    }

    if (successes.length) {
      this.log.debug({ count: successes.length }, 'Hashing succeeded');
      this.options.filesRepo.applyHashResults(successes);
    }
    if (failures.length) {
      this.log.warn({ count: failures.length }, 'Hashing failed for files');
      this.options.filesRepo.markDirtyByIds(failures);
    }

    return true;
  }
}
